import { Component, Input, OnChanges, SimpleChanges, CUSTOM_ELEMENTS_SCHEMA, OnInit, NgZone, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';
import { CarouselModalComponent } from '@app/shared/components/carousel-modal/carousel-modal.component';
import { HttpImgSrcPipe } from '@app/pipes/http-img-src.pipe';
import { Medium } from '@app/shared/components/formly-components/formly-field-picture/formly-field-picture.component';

@Component({
  selector: 'app-mosaic-gallery',
  templateUrl: './mosaic-gallery.component.html',
  styleUrls: ['./mosaic-gallery.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule, 
    IonicModule,
    HttpImgSrcPipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MosaicGalleryComponent implements OnInit, OnChanges {
  @Input() media: Medium[] = [];
  @Input() thumbnailSize: 'small' | 'medium' | 'large' = 'medium';
  @Input() maxVisible: number = 5;

  public visibleImages: Medium[] = [];
  public remainingCount: number = 0;

  constructor(private modalController: ModalController,
              private ngZone: NgZone) {}


  ngOnInit(): void {
    this.updateGallery();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['media']) {
      this.updateGallery();
    }
  }

  private updateGallery(): void {
    this.visibleImages = this.media.slice(0, this.maxVisible);
    this.remainingCount = Math.max(0, this.media.length - this.maxVisible);
  }

  async openCarousel(startIndex: number = 0): Promise<void> {
    console.log('Opening carousel at index:', startIndex, 'with media:', this.media);
    
    try {
      // Create and present modal OUTSIDE Angular zone to avoid Zone.js infinite loop
      const modal = await this.ngZone.runOutsideAngular(async () => {
        const m = await this.modalController.create({
          component: CarouselModalComponent,
          componentProps: {
            media: this.media,
            initialSlide: startIndex
          },
          cssClass: 'resizable-modal',
          animated: true,
          backdropDismiss: true
        });

        this.applySavedModalSize(m, 'carouselModalSize', 0.9, 0.8);

        console.log('Modal created, presenting...');
        await m.present();
        this.applySavedModalSize(m, 'carouselModalSize', 0.9, 0.8);
        console.log('Modal presented');
        return m;
      });

      modal.onDidDismiss().then(() => {
        this.ngZone.run(() => {
          console.log('Carousel dismissed');
        });
      });
    } catch (error) {
      console.error('Error opening carousel:', error);
    }
  }

  onImageClick(fileId: string): void {
    const idx = this.media.findIndex(medium => medium.fileId === fileId);
    if (idx === -1) {
      return;
    }
    const index = idx < this.maxVisible ? idx : this.maxVisible - 1;
    this.openCarousel(index);
  }

  onOverlayClick(): void {
    this.openCarousel(this.maxVisible - 1);
  }

  private applySavedModalSize(modal: HTMLIonModalElement, storageKey: string, widthRatio: number, heightRatio: number): void {
    const fallbackWidth = Math.round(window.innerWidth * widthRatio);
    const fallbackHeight = Math.round(window.innerHeight * heightRatio);
    const size = this.readModalSize(storageKey);
    const width = size?.width ?? fallbackWidth;
    const height = size?.height ?? fallbackHeight;
    modal.style.setProperty('--width', `${width}px`);
    modal.style.setProperty('--height', `${height}px`);
    modal.style.setProperty('--min-width', '320px');
    modal.style.setProperty('--min-height', '240px');
  }

  private readModalSize(storageKey: string): { width: number; height: number } | null {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as { width?: number; height?: number };
      if (!parsed.width || !parsed.height) {
        return null;
      }
      return { width: parsed.width, height: parsed.height };
    } catch {
      return null;
    }
  }
}
